import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { controlKeys } from "../../../../api/queryKeys";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import ConfirmationDialog from "../../../../components/dialogs/ConfirmationDialog";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { NotificationContext } from "../../../../contexts/NotificationContext";
import { ReactComponent as CheckedIcon } from "../../../../icons/icon_files//checkedIcon.svg";
import { ReactComponent as UnCheckedIcon } from "../../../../icons/icon_files/checkBoxIcon.svg";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { PLATFORM_USER_ACTIONS } from "../../../../utils/constants";
import { icons } from "../../../stack/environment/icons/iconExport";
import IntegrationImage from "../../../stack/integrations/components/IntegrationImage";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import IntegrationRowFilter from "../../steps/components/IntegrationRowFilter";
const ScanMenuButton = ({
  isScanLoading,
  loadingMessage,
  setLoading,
  gapIdsToScan,
  gapIdsToSnooze,
  tooltipText = null,
  showLabel = false,
  ...props
}) => {
  const { isLoading, setIsLoading, setIsScanning, setTaskId, scanFinished } =
    useContext(NotificationContext);
  const colors = useColors();
  const [anchorEl, setAnchorEl] = useState(null);

  const scanMessage = "Scanning in the background...";

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.SCAN_CONTROLS
  );

  const {
    requestFullScan: requestFullScanApi,
    requestPartialScan,
    requestPartialScanByTools: requestPartialScanByToolsApi,
    trackEvent,
    fetchAllControls,
  } = useAPI();

  const { data: controls } = useQuery({
    queryKey: controlKeys.all,
    queryFn: fetchAllControls,
    select: (data) =>
      data.data.map((control) => ({
        ...control,
        id: control.control_id,
      })),
  });

  const [selectedTools, setSelectedTools] = useState([]);
  const [toolsOfGaps, setToolsOfGaps] = useState([]);
  const [toolsToScan, setToolsToScan] = useState([]);
  const [openDialogForScanTools, setOpenDialogForScanTools] = useState(false);
  const [openDialogForScanControls, setOpenDialogForScanControls] =
    useState(false);

  const allSelected = selectedTools.length === toolsOfGaps.length;
  const orderedTools = [...toolsOfGaps].sort();
  const { integrations, isIntegratedLoading } = useIsIntegrated();

  useEffect(() => {
    if (gapIdsToScan && controls && gapIdsToScan.length > 0) {
      const controlsToScan = controls.filter((control) =>
        gapIdsToScan.includes(control.control_id)
      );
      if (controlsToScan.length > 0) {
        setToolsToScan(controlsToScan.map((control) => control.tool));
      }
    }
  }, [controls, gapIdsToScan]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  useEffect(() => {
    if (scanFinished) {
      setToolsToScan([]);
    }
  }, [scanFinished]);
  useEffect(() => {
    if (integrations && !isIntegratedLoading) {
      let filteredTools = integrations.filter(
        (tool) => IntegrationsMapping[tool]?.scannable
      );
      setToolsOfGaps(filteredTools);
    }
  }, [integrations, isIntegratedLoading]);

  const requestFullScan = () => {
    setIsLoading(true);

    const message = 'User run "SCAN" for all controls (FullScan)';
    trackEvent(message).then();
    requestFullScanApi().then((result) => {
      setSelectedTools([]);
      setIsScanning(true);
      Toast.info(scanMessage);
      setTaskId(result.data);
    });
  };

  const requestPartialScanByGaps = () => {
    setIsLoading(true);

    const message = 'User run "SCAN" for controls: ' + gapIdsToScan;
    trackEvent(message).then();
    requestPartialScan(gapIdsToScan).then((result) => {
      setIsScanning(true);
      Toast.info(scanMessage);
      setTaskId(result.data);
    });
  };

  const requestPartialScanByTools = () => {
    setIsLoading(true);
    const message = 'User run "SCAN" for tools: ' + selectedTools;
    trackEvent(message).then();
    requestPartialScanByToolsApi(selectedTools).then((result) => {
      setSelectedTools([]);
      setIsScanning(true);
      Toast.info(scanMessage);
      setTaskId(result.data);
    });
  };

  const handleMenuScanClick = async (event) => {
    if (!isLoading) {
      if (gapIdsToScan.length === 0) {
        setAnchorEl(event.currentTarget);
      } else {
        setOpenDialogForScanControls(true);
      }
    }
  };

  const handleScanClick = async () => {
    if (allSelected) {
      handleScanMenuClose();
      requestFullScan();
    } else if (selectedTools.length > 0) {
      handleScanMenuClose();
      requestPartialScanByTools();
    }
  };

  const ScanButton = () => {
    return (
      <Typography
        variant="caption"
        sx={{
          color: colors.primary_main,
          textAlign: "center",
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          padding: "12px 8px 8px 8px",
        }}
        onClick={() => setOpenDialogForScanTools(true)}
      >
        Apply and Scan
      </Typography>
    );
  };

  const handleScanMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCheckboxChange = (toolName) => {
    let updatedSelectedTools;
    if (selectedTools.includes(toolName)) {
      updatedSelectedTools = selectedTools.filter(
        (selectedTool) => selectedTool !== toolName
      );
    } else {
      updatedSelectedTools = [...selectedTools, toolName];
    }

    setSelectedTools(updatedSelectedTools);
    setToolsToScan(updatedSelectedTools);
  };

  const handleAllCheckboxChange = () => {
    if (allSelected) {
      setSelectedTools([]);
      setToolsToScan([]);
    } else {
      setSelectedTools(orderedTools);
      setToolsToScan(orderedTools);
    }
  };

  const handleDialogClose = () => {
    setOpenDialogForScanTools(false);
    setOpenDialogForScanControls(false);
  };

  const handleConfirm = () => {
    if (openDialogForScanControls) {
      requestPartialScanByGaps();
    } else if (openDialogForScanTools) {
      handleScanClick();
    }
    handleDialogClose();
  };

  return (
    <>
      {/*  TODO: Future work - indicate it has a dropdown //
      https://linear.app/breeze-security/issue/DEV-633/update-ui-for-scan-button */}

      <CustomToolTipWithPermission
        title={
          tooltipText
            ? tooltipText
            : gapIdsToScan.length > 0
            ? "Scan only selected"
            : gapIdsToScan.length === 0
            ? isLoading
              ? loadingMessage
              : showLabel
              ? ""
              : "Scan"
            : ""
        }
        permission={hasPermission}
        placement="top"
      >
        <span>
          <IconButton
            sx={{
              background: colors.common_white,
              color:
                !hasPermission || isScanLoading
                  ? colors.action_disabled
                  : colors.action_active,
              height: "32px",
              width: "auto",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "0 8px",
              border: "1px solid " + colors.enabled_stroke,
              borderRadius: "8px",
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                background: colors.grey_8percent,
                borderColor: colors.gray_main,
              },
              ...props.sx,
            }}
            onClick={handleMenuScanClick}
            disabled={!hasPermission || isScanLoading}
            style={{ textTransform: "none" }}
            {...props}
          >
            {!isLoading ? (
              <>
                <SvgToIcon
                  IconLogo={icons.ScanIcon}
                  style={{ width: "18px" }}
                />
                {showLabel && (
                  <Typography
                    variant="overline"
                    sx={{ textTransform: "none", fontWeight: "500" }}
                  >
                    Scan
                  </Typography>
                )}
              </>
            ) : (
              <Box
                display="flex"
                flexDirection={"row"}
                gap={1}
                alignItems={"center"}
              >
                <LoadingIcon size={5} type="three-dots" />
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.text_secondary,
                  }}
                >
                  Scanning
                </Typography>
                {toolsToScan.length > 0 && (
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={0.5}
                    alignItems="center"
                  >
                    {toolsToScan.slice(0, 3).map((toolName) => (
                      <Box
                        key={toolName}
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        borderRadius="4px"
                        border={"0.5px solid " + colors.icon_stroke}
                        height="18px"
                        width="18px"
                      >
                        <IntegrationImage
                          name={toolName}
                          width={18}
                          style={{ padding: "2.5px" }}
                          showToolTip={false}
                          newLogo={true}
                        />
                      </Box>
                    ))}
                    {toolsToScan.length > 3 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text_secondary,
                        }}
                      >
                        +{toolsToScan.length - 3}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </IconButton>
        </span>
      </CustomToolTipWithPermission>

      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleScanMenuClose}
      >
        <Box sx={{ padding: "8px 10px" }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  onChange={handleAllCheckboxChange}
                  icon={<UnCheckedIcon />}
                  checkedIcon={<CheckedIcon />}
                />
              }
              label={
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  alignItems={"center"}
                  justifyContent={"space-around"}
                >
                  <IntegrationImage
                    name={"breeze"}
                    width={30}
                    showToolTip={false}
                  />
                  <Typography
                    variant="caption"
                    style={{
                      textTransform: "none",
                      fontSize: "12px",
                      paddingLeft: "8px",
                    }}
                  >
                    All
                  </Typography>
                </Box>
              }
            />
            {orderedTools.map((toolName) => (
              <FormControlLabel
                key={toolName}
                control={
                  <Checkbox
                    checked={selectedTools.includes(toolName)}
                    onChange={() => handleCheckboxChange(toolName)}
                    icon={<UnCheckedIcon />}
                    checkedIcon={<CheckedIcon />}
                  />
                }
                label={
                  <Typography variant="caption">
                    <IntegrationRowFilter name={toolName} />
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Box>
        <Divider sx={{ margin: "0px 8px" }} />
        <ScanButton />
      </Menu>
      <ConfirmationDialog
        openDialog={openDialogForScanControls || openDialogForScanTools}
        handleDialogClose={handleDialogClose}
        handleConfirm={handleConfirm}
        dialogTitle="Confirmation"
        dialogContentFirstLine="Are you sure you want to start the scan?"
        dialogContentSecondLine="This action can take a few minutes."
        confirmationButtonText="Scan"
      />
    </>
  );
};

export default ScanMenuButton;
